import { useState } from 'react';

import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import { ClubAffiliationRequestStatus, ClubPlan } from '@shared/enums/domain.enum';
import type { Club, ClubAffiliationRequest } from '@shared/types/entities';
import { Calendar, CheckCircle, Clock, Mail, MapPin, MessageSquare, Phone, XCircle } from 'lucide-react';

import Dialog from '../ui/Dialog';

// ─── Helpers ─────────────────────────────────────────────────────
const GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-red-600',
  'from-purple-500 to-indigo-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-blue-600',
  'from-rose-500 to-indigo-600',
  'from-violet-500 to-purple-600',
];

function clubGradient(id: number): string {
  return GRADIENTS[id % GRADIENTS.length];
}

function clubLocation(city?: string | null, dept?: string | null, postcode?: string | null): string {
  return [city, postcode ?? dept].filter(Boolean).join(', ') || '-';
}

const REQ_STATUS_CONFIG: Record<
  ClubAffiliationRequestStatus,
  { label: string; bg: string; text: string; icon: typeof CheckCircle }
> = {
  [ClubAffiliationRequestStatus.PENDING]: {
    label: 'En attente',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: Clock,
  },
  [ClubAffiliationRequestStatus.ACCEPTED]: {
    label: 'Acceptée',
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    icon: CheckCircle,
  },
  [ClubAffiliationRequestStatus.REJECTED]: {
    label: 'Refusée',
    bg: 'bg-red-100',
    text: 'text-red-700',
    icon: XCircle,
  },
};

// ─── Reject Modal ────────────────────────────────────────────────
interface RejectModalProps {
  request: ClubAffiliationRequest | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  loading: boolean;
}

export function RejectModal({ request, onConfirm, onClose, loading }: RejectModalProps) {
  const [reason, setReason] = useState('');

  return (
    <Dialog open={!!request} onClose={onClose} title='Refuser la demande' className='max-w-md'>
      <div className='p-6'>
        <h3 className='mb-1 text-lg font-bold text-slate-900'>Refuser la demande</h3>
        <p className='mb-4 text-sm text-slate-500'>
          Club : <span className='font-semibold text-slate-700'>{request?.club?.name}</span>
        </p>
        <label className='mb-1 block text-sm font-medium text-slate-700'>
          Motif du refus <span className='text-slate-400'>(optionnel)</span>
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder='Ex : secteur géographique non couvert, capacité maximale atteinte…'
          className='w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-red-400 focus:outline-none'
        />
        <div className='mt-4 flex gap-3'>
          <button
            onClick={onClose}
            className='flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className='flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50'
          >
            {loading ? 'Refusé' : 'Confirmer le refus'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Affiliation Request Row ─────────────────────────────────────
interface RequestRowProps {
  request: ClubAffiliationRequest;
  onAccept: (id: number) => void;
  onReject: (request: ClubAffiliationRequest) => void;
  accepting: boolean;
}

export function RequestRow({ request, onAccept, onReject, accepting }: RequestRowProps) {
  const cfg = REQ_STATUS_CONFIG[request.status];
  const Icon = cfg.icon;
  const club = request.club;

  return (
    <div className='grid grid-cols-12 items-center gap-4 border-b border-slate-50 px-6 py-4 transition hover:bg-slate-50'>
      {/* Club info */}
      <div className='col-span-4 flex min-w-0 items-center gap-3'>
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${clubGradient(request.clubId)} text-sm font-bold text-white`}
        >
          {club ? getNameInitials(club.name) : '?'}
        </div>
        <div className='min-w-0'>
          <p className='truncate font-semibold text-slate-900'>{club?.name ?? '-'}</p>
          <div className='mt-0.5 flex items-center gap-1 text-xs text-slate-400'>
            <MapPin className='h-3 w-3 shrink-0' />
            <span className='truncate'>{clubLocation(club?.city, club?.department)}</span>
          </div>
        </div>
      </div>

      {/* Message */}
      <div className='col-span-3 min-w-0'>
        {request.message ? (
          <div className='flex items-start gap-1.5 text-sm text-slate-600'>
            <MessageSquare className='mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400' />
            <span className='line-clamp-2'>{request.message}</span>
          </div>
        ) : (
          <span className='text-xs italic text-slate-300'>Aucun message</span>
        )}
        {request.rejectedReason && (
          <p className='mt-1 line-clamp-1 text-xs text-red-500'>Motif : {request.rejectedReason}</p>
        )}
      </div>

      {/* Status */}
      <div className='col-span-2'>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`}
        >
          <Icon className='h-3.5 w-3.5' />
          {cfg.label}
        </span>
      </div>

      {/* Date */}
      <div className='col-span-1 text-xs text-slate-400'>{formatDateAbbreviatedFr(request.createdAt)}</div>

      {/* Actions */}
      <div className='col-span-2 flex items-center justify-end gap-2'>
        {request.status === ClubAffiliationRequestStatus.PENDING && (
          <>
            <button
              onClick={() => onAccept(request.id)}
              disabled={accepting}
              aria-label={`Accepter la demande du club ${club?.name ?? ''}`}
              title='Accepter'
              className='flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50'
            >
              <CheckCircle className='h-3.5 w-3.5' />
              Accepter
            </button>
            <button
              onClick={() => onReject(request)}
              disabled={accepting}
              aria-label={`Refuser la demande du club ${club?.name ?? ''}`}
              title='Refuser'
              className='flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50'
            >
              <XCircle className='h-3.5 w-3.5' />
              Refuser
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Club Card ───────────────────────────────────────────────────
export function ClubCard({ club }: { club: Club }) {
  const gradient = clubGradient(club.id);

  return (
    <div className='flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-blue-200 hover:shadow-lg'>
      <div className='mb-4 flex items-start gap-4'>
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-lg font-bold text-white`}
        >
          {getNameInitials(club.name)}
        </div>
        <div className='min-w-0 flex-1'>
          <h3 className='truncate font-bold text-slate-900'>{club.name}</h3>
          {club.alias && <p className='truncate font-mono text-xs text-slate-400'>{club.alias}</p>}
          <div className='mt-1 flex items-center gap-1 text-sm text-slate-500'>
            <MapPin className='h-3.5 w-3.5 shrink-0' />
            <span className='truncate'>{clubLocation(club.city, club.department, club.postcode)}</span>
          </div>
        </div>
      </div>

      <div className='grid flex-1 grid-cols-2 gap-3 border-t border-slate-100 py-4'>
        <div>
          <p className='mb-1 flex items-center gap-1 text-xs text-slate-400'>
            <Calendar className='h-3 w-3' />
            Affili&eacute; le
          </p>
          <p className='text-sm font-semibold text-slate-800'>{formatDateAbbreviatedFr(club.createdAt)}</p>
        </div>
        <div>
          <p className='mb-1 text-xs text-slate-400'>Plan</p>
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
              club.clubPlan === ClubPlan.PRO ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {club.clubPlan === ClubPlan.PRO ? 'Pro' : 'Gratuit'}
          </span>
        </div>
        {club.email && (
          <div className='col-span-2'>
            <p className='mb-1 flex items-center gap-1 text-xs text-slate-400'>
              <Mail className='h-3 w-3' />
              Email
            </p>
            <p className='truncate text-sm text-slate-700'>{club.email}</p>
          </div>
        )}
        {club.phone && !club.email && (
          <div className='col-span-2'>
            <p className='mb-1 flex items-center gap-1 text-xs text-slate-400'>
              <Phone className='h-3 w-3' />
              T&eacute;l&eacute;phone
            </p>
            <p className='text-sm text-slate-700'>{club.phone}</p>
          </div>
        )}
      </div>

      <div className='flex items-center justify-between border-t border-slate-100 pt-4'>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
            club.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {club.isActive ? <CheckCircle className='h-3.5 w-3.5' /> : <XCircle className='h-3.5 w-3.5' />}
          {club.isActive ? 'Actif' : 'Inactif'}
        </span>
        {club.registrationNumber && (
          <span className='max-w-[120px] truncate font-mono text-xs text-slate-400'>{club.registrationNumber}</span>
        )}
      </div>
    </div>
  );
}

export function ClubCardSkeleton() {
  return (
    <div className='flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6'>
      <div className='flex items-start gap-4'>
        <div className='h-14 w-14 shrink-0 animate-pulse rounded-xl bg-slate-200' />
        <div className='flex-1 space-y-2'>
          <div className='h-4 w-3/4 animate-pulse rounded bg-slate-200' />
          <div className='h-3 w-1/2 animate-pulse rounded bg-slate-200' />
        </div>
      </div>
      <div className='grid grid-cols-2 gap-3 border-t border-slate-100 pt-4'>
        <div className='h-8 animate-pulse rounded bg-slate-200' />
        <div className='h-8 animate-pulse rounded bg-slate-200' />
      </div>
      <div className='flex justify-between border-t border-slate-100 pt-4'>
        <div className='h-6 w-16 animate-pulse rounded-full bg-slate-200' />
      </div>
    </div>
  );
}
